import { MessageCircle } from "lucide-react";

/**
 * Click-to-chat via WhatsApp. `number` must be international digits only
 * (e.g. 5926001234). The page URL is pre-filled in the message, along with
 * an optional product/page `label` for context. Pass `shareUrl` to override
 * the URL embedded in the message — use this on product pages so the recipient
 * gets a proper OG preview (product image + title) instead of the generic
 * site-wide preview that the SPA URL would yield.
 */
export function buildWaLink(number, label, shareUrl) {
  if (!number) return null;
  const digits = String(number).replace(/\D+/g, "");
  if (digits.length < 7) return null;
  const url = shareUrl || (typeof window !== "undefined" ? window.location.href : "");
  const message = label
    ? `Hi! I'm interested in "${label}" — ${url}`
    : `Hi! I have a question about this page — ${url}`;
  return `https://wa.me/${digits}?text=${encodeURIComponent(message)}`;
}

/** Inline button (e.g. placed next to Add to Cart). */
export function WhatsAppButton({ number, label, shareUrl, className = "", testid = "whatsapp-btn" }) {
  const href = buildWaLink(number, label, shareUrl);
  if (!href) return null;
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      data-testid={testid}
      className={`inline-flex items-center justify-center gap-2 h-12 px-5 bg-[#25D366] hover:bg-[#1ebe5d] text-white font-semibold transition-colors ${className}`}
    >
      <WhatsAppIcon className="w-5 h-5" />
      Chat on WhatsApp
    </a>
  );
}

/** Floating bubble — bottom-right, sticky while scrolling. */
export function WhatsAppFloating({ number, label, shareUrl, testid = "whatsapp-float" }) {
  const href = buildWaLink(number, label, shareUrl);
  if (!href) return null;
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      data-testid={testid}
      aria-label="Chat on WhatsApp"
      className="fixed bottom-6 right-6 z-40 w-14 h-14 rounded-full bg-[#25D366] hover:bg-[#1ebe5d] text-white shadow-[0_8px_24px_rgba(37,211,102,0.45)] grid place-items-center transition-transform hover:scale-110"
    >
      <WhatsAppIcon className="w-7 h-7" />
      <span className="absolute -top-1 -right-1 w-3 h-3 bg-[#FF2A00] rounded-full ring-2 ring-white animate-pulse" />
    </a>
  );
}

/** Official-style WhatsApp glyph (inline SVG, no external assets). */
export function WhatsAppIcon({ className = "" }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.372-.025-.521-.075-.149-.669-1.612-.916-2.207-.242-.58-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347zm-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884zm8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
    </svg>
  );
}
